import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import {
  getVendorEmailRecipients,
  sendVendorPortalUpdateEmail,
} from "../../services/catalogServices";

const MODO_TUTTI = "tutti";
const MODO_SELEZIONE = "selezione";

// Nessun invio (0 inviate, 0 fallite) non e' un successo: significa nessun destinatario
const esitoSeverity = (esito) => {
  if (esito.failed > 0) return "error";
  if (esito.sent === 0) return "warning";
  return "success";
};

const etichettaDestinatario = (recipient) =>
  recipient.businessName ? `${recipient.businessName} (${recipient.email})` : recipient.email;

const ComunicazioniVendorSettings = () => {
  const [destinatari, setDestinatari] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [modo, setModo] = useState(MODO_TUTTI);
  const [selezionati, setSelezionati] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [esito, setEsito] = useState(null);
  const [errore, setErrore] = useState("");

  const caricaDestinatari = useCallback(async () => {
    setCaricamento(true);
    try {
      setDestinatari(await getVendorEmailRecipients());
    } catch (e) {
      setErrore("Impossibile caricare l'elenco dei vendor.");
    } finally {
      setCaricamento(false);
    }
  }, []);

  useEffect(() => {
    caricaDestinatari();
  }, [caricaDestinatari]);

  const inviaATutti = modo === MODO_TUTTI;
  const destinatariEffettivi = inviaATutti ? destinatari : selezionati;
  const numeroDestinatari = destinatariEffettivi.length;
  const invioBloccato = sending || caricamento || numeroDestinatari === 0;

  const handleConfirm = async () => {
    setConfirmOpen(false);
    setSending(true);
    setEsito(null);
    setErrore("");
    try {
      // Lista vuota: il backend interpreta l'assenza di scelta come "tutti gli account vendor"
      const emails = inviaATutti ? [] : selezionati.map((r) => r.email);
      setEsito(await sendVendorPortalUpdateEmail(emails));
    } catch (e) {
      setErrore(
        e?.response?.data?.message || "Invio non riuscito. Riprova tra qualche minuto."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: "12px", border: "1px solid #e2e8f0" }}>
      <Typography sx={{ fontWeight: 700, color: "#0b3d87", fontSize: "1.1rem", mb: 1 }}>
        Attivazione account vendor
      </Typography>
      <Typography sx={{ color: "#64748b", fontSize: "0.95rem", mb: 3 }}>
        Invia agli account vendor un'email che annuncia il portale online e li invita a
        completare l'attivazione impostando la password. Ogni email contiene un link personale,
        valido alcuni giorni. Le email partono una per destinatario: su liste lunghe l'invio
        richiede qualche minuto.
      </Typography>

      <RadioGroup value={modo} onChange={(e) => setModo(e.target.value)} sx={{ mb: 2 }}>
        <FormControlLabel
          value={MODO_TUTTI}
          control={<Radio sx={{ "&.Mui-checked": { color: "#0b3d87" } }} />}
          label={
            caricamento
              ? "Tutti gli account vendor"
              : `Tutti gli account vendor (${destinatari.length})`
          }
        />
        <FormControlLabel
          value={MODO_SELEZIONE}
          control={<Radio sx={{ "&.Mui-checked": { color: "#0b3d87" } }} />}
          label="Solo alcuni vendor"
        />
      </RadioGroup>

      {modo === MODO_SELEZIONE ? (
        <Autocomplete
          multiple
          options={destinatari}
          loading={caricamento}
          value={selezionati}
          onChange={(event, valore) => setSelezionati(valore)}
          getOptionLabel={etichettaDestinatario}
          isOptionEqualToValue={(option, value) => option.email === value.email}
          renderTags={(valore, getTagProps) =>
            valore.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.email}
                label={option.businessName || option.email}
                size="small"
              />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} label="Vendor destinatari" placeholder="Cerca per nome o email" />
          )}
          sx={{ mb: 3 }}
        />
      ) : null}

      <Button
        variant="contained"
        startIcon={sending ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <EmailIcon />}
        disabled={invioBloccato}
        onClick={() => setConfirmOpen(true)}
        sx={{
          backgroundColor: "#0b3d87",
          textTransform: "none",
          fontWeight: 700,
          borderRadius: "8px",
          "&:hover": { backgroundColor: "#092f6a" },
        }}
      >
        {sending ? "Invio in corso..." : "Invia email ai vendor"}
      </Button>

      {esito ? (
        <Alert severity={esitoSeverity(esito)} sx={{ mt: 3 }}>
          {esito.sent === 0 && esito.failed === 0 ? (
            "Nessun destinatario: non risultano vendor con un indirizzo email."
          ) : (
            <>
              Email inviate: <strong>{esito.sent}</strong>
              {esito.failed > 0 ? (
                <>
                  {" — non riuscite: "}
                  <strong>{esito.failed}</strong>
                  {/* Solo gli indirizzi: la causa tecnica resta nei log del servizio */}
                  {esito.failedRecipients?.length ? (
                    <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2, fontSize: "0.85rem" }}>
                      {esito.failedRecipients.map((fallito) => (
                        <li key={fallito.email}>{fallito.email}</li>
                      ))}
                    </Box>
                  ) : null}
                </>
              ) : null}
            </>
          )}
        </Alert>
      ) : null}

      {errore ? (
        <Alert severity="error" sx={{ mt: 3 }}>
          {errore}
        </Alert>
      ) : null}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#0b3d87" }}>Confermi l'invio?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            L'email verrà inviata ai seguenti <strong>{numeroDestinatari}</strong>
            {numeroDestinatari === 1 ? " account vendor" : " account vendor"}.
            L'operazione non è annullabile.
          </DialogContentText>
          <Box
            sx={{
              maxHeight: 280,
              overflowY: "auto",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              p: 1.5,
              backgroundColor: "#f8fafc",
            }}
          >
            {destinatariEffettivi.map((recipient) => (
              <Box key={recipient.email} sx={{ py: 0.5, fontSize: "0.9rem" }}>
                <Box component="span" sx={{ color: "#0f172a" }}>
                  {recipient.email}
                </Box>
                {recipient.businessName ? (
                  <Box component="span" sx={{ color: "#64748b" }}>
                    {` — ${recipient.businessName}`}
                  </Box>
                ) : null}
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: "none", color: "#64748b" }}>
            Annulla
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            sx={{
              backgroundColor: "#0b3d87",
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { backgroundColor: "#092f6a" },
            }}
          >
            Invia
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ComunicazioniVendorSettings;
